import type { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'

import type { ISectionRichTextFields } from 'contentful-shared'

import { Amount } from '../../elements/Amount'
import { ArrowLink } from '../../elements/ArrowLink'
import { Type } from '../../elements/Typography/Typography'
import { RichTextRenderer } from '../../richText/RichTextRenderer'
import { Appear } from '../Appear'
import { Column } from '../Column'
import { Row } from '../Row'

type RichTextSectionProps = Pick<ISectionRichTextFields, 'content'>

const options: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Type as="p" preset="textLarge" mTop={{ xs: 1 }}>
        {children}
      </Type>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Type
        as="h2"
        preset="headlineLarge"
        mTop={{ xs: 2.5, md: 4 }}
        mBottom={{ xs: 1, md: 2 }}
      >
        {children}
      </Type>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Type
        as="h3"
        preset="headlineMedium"
        mTop={{ xs: 2.5, md: 4 }}
        mBottom={{ xs: 1, md: 1.5 }}
      >
        {children}
      </Type>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <Type
        as="h4"
        preset="headlineSmall"
        mTop={{ xs: 2.5, md: 4 }}
        mBottom={{ xs: 1 }}
      >
        {children}
      </Type>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const {
        data: { uri },
      } = node
      return (
        <ArrowLink to={uri} size="inherit">
          {children}
        </ArrowLink>
      )
    },
    [INLINES.EMBEDDED_ENTRY]: node => {
      const { sys, fields } = node.data.target
      switch (sys.contentType.sys.id) {
        case 'prices':
          return (
            <Amount
              value={fields.isk}
              format={fields.priceFormat}
              fallback={null}
              useSymbol
            />
          )
        default:
          return null
      }
    },
  },
}

export const RichTextSection = ({ content }: RichTextSectionProps) => {
  const hasTable = content.content?.some(
    block => block?.nodeType === BLOCKS.TABLE
  )
  return (
    <Appear observer>
      <Appear>
        <Row>
          <Column
            {...(!hasTable && {
              size: { xs: 1, md: 8 / 12 },
              $offsetLeft: 2 / 12,
            })}
          >
            <RichTextRenderer document={content} customOptions={options} />
          </Column>
        </Row>
      </Appear>
    </Appear>
  )
}
